import { useEffect, useState } from "react";
import {
  CreditCardOutlined,
  UserOutlined,
  SnippetsOutlined,
  ApartmentOutlined,
  BankOutlined,
  PlusOutlined,
  MenuOutlined,
  GlobalOutlined,
  DropboxOutlined,
  FormOutlined,
  SettingOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { Menu, Button, Drawer, Form, Input } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import axios from "axios";
import { toast } from "react-toastify";
import { loginUser, logoutUser } from "../../reducers/userSlice";
import { estoreDet } from "../../reducers/estoreSlice";
import { appendEstore, removeEstore } from "../../reducers/estoresSlice";

const MainMenu = ({ menu }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const estore = useSelector((state) => state.estore);
  const estores = useSelector((state) => state.estores);
  const [form] = Form.useForm();

  const [showPrime, setShowPrime] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("cosmic");
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const date1 = new Date(estore.createdAt);
    const date2 = new Date();
    const timeDifference = date2.getTime() - date1.getTime();
    const daysDifference = Math.round(timeDifference / (1000 * 3600 * 24));

    if (daysDifference && daysDifference > 380) setShowPrime(true);
  }, [estore && estore._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Listen for changes in selected product
    const storedProduct = localStorage.getItem("selectedProduct");
    if (storedProduct) {
      setSelectedProduct(storedProduct);
    }

    // Listen for storage changes
    const handleStorageChange = () => {
      const newProduct = localStorage.getItem("selectedProduct");
      if (newProduct) {
        setSelectedProduct(newProduct);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom event for same-tab updates
    const handleProductChange = (event) => {
      setSelectedProduct(event.detail);
    };

    window.addEventListener("productChanged", handleProductChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("productChanged", handleProductChange);
    };
  }, []);

  const logout = () => {
    dispatch(removeEstore(estore && estore._id));
    localStorage.removeItem("userToken");
    dispatch(logoutUser());
    navigate("/");
  };

  const switchEstore = (account) => {
    if (!account || !account.user || !account.estore) return;

    setMobileMenuOpen(false);
    localStorage.setItem("userToken", account.token);
    dispatch(loginUser(account.user));
    dispatch(estoreDet(account.estore));
    navigate("/stores");
    window.location.reload(); // Reload the page to ensure all state is updated
  };

  const onAddAccount = async (values) => {
    setAddingAccount(true);
    try {
      const tokenRes = await axios.post(
        process.env.REACT_APP_RESELL_API + "/gratis/auth-login",
        values,
      );

      if (tokenRes.data.err) {
        toast.error(tokenRes.data.err);
        setAddingAccount(false);
        return;
      }

      const token = tokenRes.data;
      const userRes = await axios.get(
        process.env.REACT_APP_RESELL_API +
          "/gratis/user-details/" +
          process.env.REACT_APP_MASTERID,
        {
          headers: {
            authToken: token,
          },
        },
      );

      if (userRes.data.err) {
        toast.error(userRes.data.err);
        setAddingAccount(false);
        return;
      }

      if (!userRes.data.estoreid.reseller.status) {
        toast.error("Sorry, your login details has no reseller account.");
        setAddingAccount(false);
        return;
      }

      const nextUser = {
        ...userRes.data,
        token,
        masterUser:
          userRes.data.estoreid._id === process.env.REACT_APP_MASTERID,
      };
      const nextEstore = userRes.data.estoreid;

      localStorage.setItem("userToken", token);
      dispatch(loginUser(nextUser));
      dispatch(estoreDet(nextEstore));
      dispatch(
        appendEstore({
          estoreId: nextEstore._id,
          storeName: nextEstore.name || nextEstore.slug,
          token,
          user: nextUser,
          estore: nextEstore,
        }),
      );

      setShowAddAccount(false);
      setMobileMenuOpen(false);
      form.resetFields();
      navigate("/stores");
      window.location.reload(); // Reload the page to ensure all state is updated
    } catch (error) {
      toast.error(
        error?.response?.data?.err ||
          "Unable to add account. Please try again.",
      );
    }

    setAddingAccount(false);
  };

  function getItem(label, key, icon, children, type) {
    return {
      key,
      icon,
      children,
      label,
      type,
    };
  }

  // Define menu items for Cosmic (default/full menu)
  const cosmicMenuItems = {
    mobile: [
      getItem("Stores", "stores", <BankOutlined />),
      getItem("Owners", "owners", <UserOutlined />),
      getItem("Packages", "package", <DropboxOutlined />),
      getItem("Billing", "billing", <SnippetsOutlined />),
      getItem("Affiliate", "affiliate", <ApartmentOutlined />),
      getItem("Payments", "payments", <CreditCardOutlined />),
      getItem("Create Guide", "trainings", <PlusOutlined />),
      getItem("Settings", "settings", <SettingOutlined />),
      getItem("Instructions", "instruct", <FormOutlined />),
      getItem("Main Website", "main-website", <GlobalOutlined />),
      getItem("Reseller Website", "reseller-website", <GlobalOutlined />),
      getItem("Add more account", "add-account", <PlusOutlined />),
      getItem("Logout", "logout", <LogoutOutlined />),
    ],
    desktop: [
      getItem("Stores", "stores", <BankOutlined />),
      getItem("Owners", "owners", <UserOutlined />),
      getItem("Packages", "package", <DropboxOutlined />),
      getItem("Billing", "billing", <SnippetsOutlined />),
      getItem("Affiliate", "affiliate", <ApartmentOutlined />),
      getItem("Payments", "payments", <CreditCardOutlined />),
      getItem("Create Guide", "trainings", <PlusOutlined />),
      getItem("Settings", "settings", <SettingOutlined />),
      getItem("Instructions", "instruct", <FormOutlined />),
      getItem("Add more account", "add-account", <PlusOutlined />),
      getItem("Logout", "logout", <LogoutOutlined />),
    ],
  };

  // Define menu items for Ingreventory (only Store)
  const ingreventoryMenuItems = {
    mobile: [
      getItem("Store", "stores", <BankOutlined />),
      getItem("Add more account", "add-account", <PlusOutlined />),
      getItem("Logout", "logout", <LogoutOutlined />),
    ],
    desktop: [
      getItem("Store", "stores", <BankOutlined />),
      getItem("Add more account", "add-account", <PlusOutlined />),
      getItem("Logout", "logout", <LogoutOutlined />),
    ],
  };

  // Select menu items based on selected product
  const items =
    selectedProduct === "ingreventory"
      ? isMobile
        ? ingreventoryMenuItems.mobile
        : ingreventoryMenuItems.desktop
      : isMobile
        ? cosmicMenuItems.mobile
        : cosmicMenuItems.desktop;

  const onClick = (e) => {
    if (e.key === "logout") {
      logout();
    } else if (e.key === "add-account") {
      setShowAddAccount((prev) => !prev);
    } else if (e.key === "main-website") {
      window.open(estore.domain);
      setMobileMenuOpen(false);
    } else if (e.key === "reseller-website") {
      window.open(estore.subdomain);
      setMobileMenuOpen(false);
    } else if (e.key === "package-a") {
    } else if (e.key === "package-b") {
    } else if (e.key === "package-c") {
    } else {
      setMobileMenuOpen(false);
      navigate(`/${e.key}`);
    }
  };

  return (
    <>
      {isMobile ? (
        <>
          <div
            style={{
              paddingTop: 75,
              paddingLeft: 5,
            }}
          >
            <Button
              style={{ fontSize: 18, height: 40 }}
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuOutlined /> Menu
            </Button>
            <Drawer
              title={`Hi! ${user.name}`}
              placement="left"
              onClose={() => setMobileMenuOpen(false)}
              open={mobileMenuOpen}
              width={280}
            >
              <Menu
                selectedKeys={[menu]}
                mode="inline"
                items={items}
                onClick={onClick}
              />
              {showAddAccount && (
                <div
                  style={{
                    marginTop: 10,
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    padding: 10,
                  }}
                >
                  <div style={{ marginBottom: 8, fontSize: 13 }}>
                    Login another account
                  </div>
                  <Form form={form} layout="vertical" onFinish={onAddAccount}>
                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { required: true, message: "Please input your email." },
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input size="small" disabled={addingAccount} />
                    </Form.Item>
                    <Form.Item
                      label="Password"
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: "Please input your password.",
                        },
                      ]}
                      style={{ marginBottom: 10 }}
                    >
                      <Input.Password size="small" disabled={addingAccount} />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button
                        htmlType="submit"
                        type="primary"
                        size="small"
                        loading={addingAccount}
                      >
                        Add Account
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              )}
              {estores && estores.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#666" }}>Accounts</div>
                  {estores.map((account) => (
                    <div
                      key={account.estoreId}
                      style={{
                        fontSize: 14,
                        marginTop: 8,
                        cursor: "pointer",
                        color:
                          estore && estore._id === account.estoreId
                            ? "#1890ff"
                            : "#111",
                      }}
                      onClick={() => switchEstore(account)}
                    >
                      {account.storeName}
                    </div>
                  ))}
                </div>
              )}
            </Drawer>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              width: 256,
              paddingTop: 20,
            }}
          >
            <div style={{ color: "#ffffff", margin: "0 0 20px 10px" }}>
              Hi! {user.name}
            </div>
            {menu && (
              <Menu
                defaultSelectedKeys={[menu]}
                mode="inline"
                theme="dark"
                items={items}
                onClick={onClick}
              />
            )}
            {showAddAccount && (
              <div
                style={{
                  margin: "10px 10px 0 10px",
                  padding: 10,
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 6,
                  background: "rgba(255,255,255,0.04)",
                }}
              >
                <div
                  style={{ color: "#ffffff", marginBottom: 8, fontSize: 12 }}
                >
                  Login another account
                </div>
                <Form form={form} layout="vertical" onFinish={onAddAccount}>
                  <Form.Item
                    label={<span style={{ color: "#ffffff" }}>Email</span>}
                    name="email"
                    rules={[
                      { required: true, message: "Please input your email." },
                    ]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input size="small" disabled={addingAccount} />
                  </Form.Item>
                  <Form.Item
                    label={<span style={{ color: "#ffffff" }}>Password</span>}
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: "Please input your password.",
                      },
                    ]}
                    style={{ marginBottom: 10 }}
                  >
                    <Input.Password size="small" disabled={addingAccount} />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      htmlType="submit"
                      type="primary"
                      size="small"
                      loading={addingAccount}
                    >
                      Add Account
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            )}
            {estores && estores.length > 0 && (
              <div
                style={{
                  color: "#ffffff",
                  margin: "6px 0 0 10px",
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.8 }}>Accounts</div>
                {estores.map((account) => (
                  <div
                    key={account.estoreId}
                    style={{
                      fontSize: 13,
                      marginTop: 6,
                      cursor: "pointer",
                      color:
                        estore && estore._id === account.estoreId
                          ? "#40a9ff"
                          : "#ffffff",
                    }}
                    onClick={() => switchEstore(account)}
                  >
                    {account.storeName}
                  </div>
                ))}
              </div>
            )}
            <div
              style={{
                color: "#ffffff",
                margin: "20px 0 20px 10px",
                fontSize: 12,
              }}
            >
              Main Website:
              <br />{" "}
              <a
                href={estore.domain}
                style={{
                  color: "#ffffff",
                }}
                target="_blank"
                rel="noreferrer"
              >
                {estore.domain}
              </a>
              <br />
              <br />
              Reseller Website:
              <br />
              <a
                href={estore.subdomain}
                style={{
                  color: "#ffffff",
                }}
                target="_blank"
                rel="noreferrer"
              >
                {estore.subdomain}
              </a>
              <div>
                <br />
                <br />
                <strong>Reseller Asking Price</strong>
                <br />
                1. Basic Account: 0%
                <br />
                2. Dedicated Account 1: 12,990
                <br />
                3. Dedicated Account 2: 32,990
                <br />
              </div>
              {showPrime && (
                <div style={{ color: "white" }}>
                  <br />
                  P.S. Stop paying Reseller Asking Price by owning the whole
                  system. Take full control by getting all the codes and solely
                  accessing the server. <br />
                  <br />
                  <Button
                    size="small"
                    style={{
                      borderRadius: 6,
                    }}
                    onClick={() =>
                      window.open("https://learnclavstore.com/prime", "_blank")
                    }
                  >
                    Learn More
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MainMenu;
